import type React from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

import { FormField, fieldDescribedBy } from '~/components/form/FormField';

export interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  type?: 'text' | 'password' | 'email' | 'number' | 'url' | 'datetime-local' | 'date';
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  placeholder?: string;
  /** text-xs text-gray-500 dark:text-gray-400. Takes nodes so a field can carry a link or inline `code`. */
  helperText?: React.ReactNode;
  /** Replaces helperText, text-xs text-error-600 dark:text-error-400 */
  error?: string;
  labelAction?: React.ReactNode;
  testId: string;
  /** Accessible name when the visible label is shared by repeated fields (e.g. one per endpoint). */
  ariaLabel?: string;
  /** Monospace — slugs, keys, codes. */
  code?: boolean;
  /** An outside element that also describes this field (its id), e.g. a notice elsewhere in the form. */
  describedBy?: string;
  /** Form field name; password managers key on it for sign-in forms. */
  name?: string;
  autoComplete?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  /** Native constraints — forwarded so the browser refuses what the API would refuse anyway. */
  pattern?: string;
  /** The message the browser shows when `pattern` fails. */
  title?: string;
  min?: number;
  max?: number;
  step?: number;
  /** Native input suggestions (a sibling `<datalist>`, wired via `list`) — offered, not
   *  enforced; the field still accepts any value. */
  suggestions?: string[];
  trailingActions?: React.ReactNode;
  /** Rewrites what is typed (e.g. uppercase a variable name). The field keeps the caret where the
   *  person was typing and waits out an IME composition before applying it. */
  transform?: (value: string) => string;
  /** Said (politely, in a line that stays mounted) once `transform` has changed what was typed. */
  transformHint?: string;
  /** Native limit; also shows a `count/limit` counter under the field. */
  maxLength?: number;
}

export function TextField({
  id,
  label,
  value,
  onChange,
  onBlur,
  type = 'text',
  required,
  readOnly,
  disabled,
  placeholder,
  helperText,
  error,
  labelAction,
  testId,
  ariaLabel,
  code,
  describedBy,
  name,
  autoComplete,
  inputMode,
  pattern,
  title,
  min,
  max,
  step,
  suggestions,
  trailingActions,
  transform,
  transformHint,
  maxLength,
}: TextFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const caret = useRef<{ start: number; end: number } | null>(null);
  const composing = useRef(false);
  const [transformed, setTransformed] = useState(false);
  const hintId = `${id}-transform-hint`;

  // A controlled input jumps its caret to the end when the value is rewritten; put it back.
  useLayoutEffect(() => {
    const el = inputRef.current;
    const target = caret.current;
    caret.current = null;
    if (el && target && document.activeElement === el)
      el.setSelectionRange(target.start, target.end);
  }, [value]);

  function emit(el: HTMLInputElement) {
    const raw = el.value;
    const next = transform ? transform(raw) : raw;
    if (next !== raw) {
      const delta = next.length - raw.length;
      const shift = (at: number | null) =>
        at === null ? null : Math.min(Math.max(at + delta, 0), next.length);
      const start = shift(el.selectionStart);
      const end = shift(el.selectionEnd);
      caret.current = start === null || end === null ? null : { start, end };
      setTransformed(true);
    }
    onChange(next);
  }

  const descriptionIds =
    [
      fieldDescribedBy(id, helperText, error, describedBy, maxLength !== undefined),
      transformHint ? hintId : undefined,
    ]
      .filter(Boolean)
      .join(' ') || undefined;
  const listId = suggestions?.length ? `${id}-suggestions` : undefined;
  return (
    <FormField
      id={id}
      label={label}
      required={required}
      labelAction={labelAction}
      helperText={helperText}
      error={error}
      counter={maxLength === undefined ? undefined : { count: value.length, limit: maxLength }}
    >
      <div className={trailingActions ? 'relative' : undefined}>
        <input
          ref={inputRef}
          id={id}
          data-testid={testId}
          aria-label={ariaLabel}
          name={name}
          type={type}
          value={value}
          onChange={(event) => {
            if (composing.current) onChange(event.target.value);
            else emit(event.target);
          }}
          onCompositionStart={() => {
            composing.current = true;
          }}
          onCompositionEnd={(event) => {
            composing.current = false;
            emit(event.currentTarget);
          }}
          onBlur={onBlur}
          required={required}
          readOnly={readOnly}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete={autoComplete}
          inputMode={inputMode}
          pattern={pattern}
          title={title}
          min={min}
          max={max}
          step={step}
          maxLength={maxLength}
          list={listId}
          aria-invalid={error ? true : undefined}
          aria-describedby={descriptionIds}
          className={`field-control read-only:text-gray-500 dark:read-only:text-gray-400 ${code ? 'font-mono' : ''} ${trailingActions ? 'pr-28' : ''}`}
        />
        {trailingActions && (
          <div className="absolute inset-y-0 right-1 flex items-center gap-0.5">
            {trailingActions}
          </div>
        )}
      </div>
      {transformHint ? (
        <p id={hintId} aria-live="polite" className={transformed ? 'field-helper' : 'sr-only'}>
          {transformed ? transformHint : ''}
        </p>
      ) : null}
      {listId ? (
        <datalist id={listId}>
          {suggestions?.map((suggestion) => (
            <option key={suggestion} value={suggestion} />
          ))}
        </datalist>
      ) : null}
    </FormField>
  );
}
